import { i18n } from "@/i18n";
import {
  SIDENAV_GROUP_BY,
  SPECIFIC_NON_SEG_USERS_WHO_CAN_ACCESS_SEG_EMAIL_HISTORY,
} from "@/constants";
import { PHISH_SERVICE, SEG_SERVICE } from "@/utils/services";
import { useFlag } from "@/composables/useFlags/useFlag";

const { isFeatureAvailable: isEmaiHistoryAvailable } = useFlag(
  "EMAIL_HISTORY_PHASE_1",
);
const { isFeatureAvailable: isSEGAvailable } = useFlag("SEG");
const { isFeatureAvailable: isBlockListAvailable } = useFlag("BLOCK_LIST");

export const MSP_NAV_ITEMS = [
  {
    order: 1,
    label: i18n.global.t("sidebar-email-security"),
    groupBy: SIDENAV_GROUP_BY.BETA,
    icon: "cl-icon-tabler-mail",
    id: "/msp/email_security",
    dataTestId: "msp-phish-titan",
    solutionId: PHISH_SERVICE,
    children: [
      {
        label: i18n.global.t("sidebar-email-sec-overview"),
        id: "/msp/email_security/overview",
        dataTestId: "msp-email_security-overview",
      },
      ...(isEmaiHistoryAvailable.value
        ? [
            {
              label: i18n.global.t("sidebar-email-history"),
              id: "/msp/email_security/email-history",
              dataTestId: "msp-email_security-email_history",
              solutionId: SEG_SERVICE,
              visibleForUuids:
                SPECIFIC_NON_SEG_USERS_WHO_CAN_ACCESS_SEG_EMAIL_HISTORY,
            },
          ]
        : []),
      {
        label: i18n.global.t("sidebar-incidents"),
        id: "/msp/email_security/incidents",
        dataTestId: "msp-email_security-incidents",
        permanentPill: { variant: "infoFull", text: i18n.global.t("AI") },
      },
      {
        label: i18n.global.t("sidebar-configuration"),
        id: "/msp/email_security/configuration",
        dataTestId: "msp-email_security-configuration",
        hasEvent: true,
        children: [
          {
            label: i18n.global.t("sidebar-mail-flow"),
            id: "/msp/email_security/configuration/mail_flow",
            dataTestId: "msp-email_security-configuration-mail-flow",
          },
          {
            label: i18n.global.t("page-title-link-lock"),
            id: "/msp/email_security/configuration/link_lock",
            dataTestId: "msp-email_security-configuration-link-lock",
            disabled: false,
          },
          ...(isBlockListAvailable.value
            ? [
                {
                  label: i18n.global.t("Block List"),
                  id: "/msp/email_security/configuration/block_list",
                  dataTestID: "msp-mail-flow-tab-block-list",
                  solutionId: SEG_SERVICE,
                },
              ]
            : []),
        ],
      },
      {
        label: i18n.global.t("sidebar-notifications"),
        id: "/msp/email_security/notifications",
        dataTestId: "msp-email_security-notifications",
      },
      {
        label: i18n.global.t("sidebar-reports"),
        id: "/msp/email_security/reports",
        dataTestId: "msp-email_security-reports",
        hasEvent: true,
        children: [
          {
            label: i18n.global.t("sidebar-summary"),
            id: "/msp/email_security/reports/summary",
            dataTestId: "msp-email_security-reports-summary",
          },
          {
            label: i18n.global.t("sidebar-insights"),
            id: "/msp/email_security/reports/insights",
            dataTestId: "msp-email_security-reports-insights",
          },
        ],
      },
    ],
  },
];

const children = [
  {
    label: i18n.global.t("sidebar-email-sec-overview"),
    id: "/customer/email_security/overview",
    dataTestId: "customer-email_security-overview",
  },
  ...(isEmaiHistoryAvailable.value
    ? [
        {
          label: i18n.global.t("sidebar-email-history"),
          id: "/customer/email_security/email-history",
          dataTestId: "customer-email_security-email_history",
          solutionId: SEG_SERVICE,
          visibleForUuids:
            SPECIFIC_NON_SEG_USERS_WHO_CAN_ACCESS_SEG_EMAIL_HISTORY,
        },
      ]
    : []),
  {
    label: i18n.global.t("sidebar-incidents"),
    id: "/customer/email_security/incidents",
    dataTestId: "customer-email_security-incidents",
    permanentPill: { variant: "infoFull", text: i18n.global.t("AI") },
  },
  {
    label: i18n.global.t("sidebar-domains"),
    id: "/customer/email_security/domains",
    dataTestId: "customer-email_security-domains",
    solutionId: SEG_SERVICE,
  },
  {
    label: i18n.global.t("sidebar-configuration"),
    id: "/customer/email_security/configuration",
    disabled: false,
    dataTestId: "customer-email_security-configuration",
    hasEvent: true,
    children: [
      {
        label: i18n.global.t("sidebar-mail-flow"),
        id: "/customer/email_security/configuration/mail_flow",
        dataTestId: "customer-email_security-configuration-mail-flow",
      },
      {
        label: i18n.global.t("page-title-link-lock"),
        id: "/customer/email_security/configuration/link_lock",
        dataTestId: "customer-email_security-link-lock",
      },
      ...(isBlockListAvailable.value
        ? [
            {
              label: i18n.global.t("Block List"),
              id: "/customer/email_security/configuration/block_list",
              dataTestID: "customer-mail-flow-tab-block-list",
              solutionId: SEG_SERVICE,
            },
          ]
        : []),
    ],
  },
  {
    label: i18n.global.t("sidebar-notifications"),
    id: "/customer/email_security/notifications",
    dataTestId: "customer-email_security-notifications",
  },
  {
    label: i18n.global.t("sidebar-reports"),
    id: "/customer/email_security/reports",
    dataTestId: "customer-email_security-reports",
    hasEvent: true,
    children: [
      {
        label: i18n.global.t("sidebar-summary"),
        id: "/customer/email_security/reports/summary",
        dataTestId: "customer-email_security-reports-summary",
      },
      {
        label: i18n.global.t("sidebar-insights"),
        id: "/customer/email_security/reports/insights",
        dataTestId: "customer-email_security-reports-insights",
      },
    ],
  },
];

// TODO remove once SEG is on prod
const childrenWithoutSEG = [
  {
    label: i18n.global.t("sidebar-email-sec-overview"),
    id: "/customer/email_security/overview",
    dataTestId: "customer-email_security-overview",
  },
  {
    label: i18n.global.t("sidebar-incidents"),
    id: "/customer/email_security/incidents",
    dataTestId: "customer-email_security-incidents",
    permanentPill: { variant: "infoFull", text: i18n.global.t("AI") },
  },
  {
    label: i18n.global.t("sidebar-configuration"),
    id: "/customer/email_security/configuration",
    disabled: false,
    dataTestId: "customer-email_security-configuration",
    hasEvent: true,
    children: [
      {
        label: i18n.global.t("sidebar-mail-flow"),
        id: "/customer/email_security/configuration/mail_flow",
        dataTestId: "customer-email_security-configuration-mail-flow",
      },
      {
        label: i18n.global.t("page-title-link-lock"),
        id: "/customer/email_security/configuration/link_lock",
        dataTestId: "customer-email_security-link-lock",
      },
    ],
  },
  {
    label: i18n.global.t("sidebar-notifications"),
    id: "/customer/email_security/notifications",
    dataTestId: "customer-email_security-notifications",
  },
  {
    label: i18n.global.t("sidebar-reports"),
    id: "/customer/email_security/reports",
    dataTestId: "customer-email_security-reports",
    hasEvent: true,
    children: [
      {
        label: i18n.global.t("sidebar-summary"),
        id: "/customer/email_security/reports/summary",
        dataTestId: "customer-email_security-reports-summary",
      },
      {
        label: i18n.global.t("sidebar-insights"),
        id: "/customer/email_security/reports/insights",
        dataTestId: "customer-email_security-reports-insights",
      },
    ],
  },
];

export const CUSTOMER_NAV_ITEMS = [
  {
    order: 1,
    label: i18n.global.t("sidebar-email-security"),
    groupBy: SIDENAV_GROUP_BY.BETA,
    icon: "cl-icon-tabler-mail",
    id: "/customer/email_security",
    dataTestId: "customer-phish-titan",
    solutionId: PHISH_SERVICE,
    children: isSEGAvailable.value ? children : childrenWithoutSEG,
  },
];

export default {};
